import { FC, useMemo, useContext } from "react";
import { IconButton, Slider, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import {
  CutAtCursorPos,
  GenerateCalculatedSequence,
  GetOrderedActionsList,
  SetHandleAudio,
  SwitchAudioPlayMode,
} from "../../Redux/Reducers/redux-slice-timetool";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import FunctionsIcon from "@mui/icons-material/Functions";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import { throttle } from "throttle-debounce";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { ModalContext } from "../../Components/Modals";
import ModalTimetoolCompilationSkipMarkers, {
  ModalTimetoolCompilationSkipMarkersElementProps,
} from "../../Components/Modals/ModalTimeToolCompilation/modal-timetool-compilation-skip-markers";
import { SequenceMetadataSegment } from "../../Core/Api/Types/sequence-metadata-segment";
import { SecToTimecode } from "../../Utils/Timecode/ReactPlayer";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import SpeakerGroupIcon from "@mui/icons-material/SpeakerGroup";
import SpeakerIcon from "@mui/icons-material/Speaker";
import { useTranslation } from "react-i18next";

const LeftTimelineActions: FC<{
  handleSliderZoom: (e: any) => void;
  handleScroll: (direction: "left" | "right") => void;
  time: number;
  fps: number;
  zoomValue: number;
  throttleSliderZoom: throttle<(e: any) => void>;
  handleAudio: boolean;
  audioPlayMode: "mono" | "stereo";
  onGenerated: (segments: SequenceMetadataSegment[]) => void;
}> = ({
  handleSliderZoom,
  handleScroll,
  audioPlayMode,
  fps,
  handleAudio,
  throttleSliderZoom,
  time,
  zoomValue,
  onGenerated,
}) => {
  const { t } = useTranslation();
  const actions = useSelector(GetOrderedActionsList);

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const dispatch = useDispatch();
  const theme = useTheme();

  const ScrollLeft = useMemo(
    () => (
      <Tooltip title={t("tooltip:title.scrollLeft")}>
        <IconButton onClick={() => handleScroll("left")}>
          <ArrowCircleLeftIcon />
        </IconButton>
      </Tooltip>
    ),
    [handleScroll]
  );

  const ScrollRight = useMemo(
    () => (
      <Tooltip title={t("tooltip:title.scrollRight")}>
        <IconButton sx={{ mr: 4 }} onClick={() => handleScroll("right")}>
          <ArrowCircleRightIcon />
        </IconButton>
      </Tooltip>
    ),
    [handleScroll]
  );

  const ZoomOut = useMemo(
    () => (
      <IconButton
        onClick={() => {
          handleSliderZoom({ target: { value: Math.max(zoomValue - 10, 1) } });
        }}
      >
        <ZoomOutIcon sx={{ mt: 1, mb: 1, mr: 0.5, color: theme.palette.grey[500] }} />
      </IconButton>
    ),
    [handleSliderZoom]
  );

  const SliderZoom = useMemo(
    () => (
      <Slider
        sx={{ width: "7vw", marginTop: "6px" }}
        size="small"
        step={1}
        min={1}
        max={95}
        value={zoomValue}
        valueLabelDisplay="off"
        onChange={throttleSliderZoom}
      />
    ),
    [zoomValue, throttleSliderZoom]
  );

  const ZoomIn = useMemo(
    () => (
      <IconButton
        onClick={() => {
          handleSliderZoom({ target: { value: Math.min(zoomValue + 10, 95) } });
        }}
      >
        <ZoomInIcon sx={{ mt: 1, mb: 1, ml: 0.5, color: theme.palette.grey[500] }} />
      </IconButton>
    ),
    [handleSliderZoom]
  );

  const Generate = useMemo(
    () => (
      <Tooltip title={t("tooltip:title.generateFinalCalculatedSequence")}>
        <IconButton sx={{ ml: 0.5 }} onClick={() => dispatch(GenerateCalculatedSequence(false))}>
          <FunctionsIcon />
        </IconButton>
      </Tooltip>
    ),
    []
  );

  const GenerateAndShuffle = useMemo(
    () => (
      <Tooltip title={t("tooltip:title.generateSequenceShuffle")}>
        <IconButton sx={{ ml: 0.5 }} onClick={() => dispatch(GenerateCalculatedSequence(true))}>
          <ShuffleIcon />
        </IconButton>
      </Tooltip>
    ),
    []
  );

  const CutAtPos = useMemo(
    () => (
      <Tooltip title={t("tooltip:title.cutAtCursorPosition")}>
        <IconButton sx={{ ml: 0.5 }} onClick={() => dispatch(CutAtCursorPos())}>
          <ContentCutIcon />
        </IconButton>
      </Tooltip>
    ),
    []
  );

  const ToggleAudio = useMemo(
    () => (
      <Tooltip title={t("tooltip:title.toggleAudio")}>
        <IconButton sx={{ ml: 0.5 }} onClick={() => dispatch(SetHandleAudio(!handleAudio))}>
          {handleAudio ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
      </Tooltip>
    ),
    [handleAudio]
  );

  const SwitchAudioMode = useMemo(
    () => (
      <Tooltip title={audioPlayMode === "mono" ? t("tooltip:title.switchToStereo") : t("tooltip:title.switchToMono")}>
        <IconButton sx={{ ml: 0.5 }} onClick={() => dispatch(SwitchAudioPlayMode())}>
          {audioPlayMode === "mono" ? <SpeakerIcon /> : <SpeakerGroupIcon />}
        </IconButton>
      </Tooltip>
    ),
    [audioPlayMode]
  );

  const SkipMarker = useMemo(
    () => (
      <Tooltip title={t("modal:title.skipMarkers")}>
        <IconButton
          sx={{ ml: 0.5 }}
          onClick={() => {
            if (!openModal) return;

            openModal<ModalTimetoolCompilationSkipMarkersElementProps>(ModalTimetoolCompilationSkipMarkers, {
              isGrouped: isSegmentsGrouped(actions.filter((x) => x.data && x.data.titleId).map((x) => x.data.titleId)),
              onGenerated: onGenerated,
            });
          }}
        >
          <PushPinOutlinedIcon />
        </IconButton>
      </Tooltip>
    ),
    [onGenerated, actions, openModal]
  );

  return (
    <Stack flex={1} display="flex" direction="row">
      <Typography marginLeft={2} marginRight={4} lineHeight="40px" variant="body1" width="60px">
        {SecToTimecode(time, fps)}
      </Typography>
      {ScrollLeft}
      {ScrollRight}
      {ZoomOut}
      {SliderZoom}
      {ZoomIn}
      {Generate}
      {GenerateAndShuffle}
      {CutAtPos}
      {ToggleAudio}
      {handleAudio && SwitchAudioMode}
      {actions != null && actions.length > 0 && SkipMarker}
    </Stack>
  );
};

const isSegmentsGrouped = (titleIds: (number | null)[]) => {
  let lastSeenTitleId: number | null = null;

  for (let i = 0; i < titleIds.length - 1; i++) {
    if (titleIds[i] !== titleIds[i + 1]) {
      lastSeenTitleId = titleIds[i];

      for (let j = i + 1; j < titleIds.length; j++) {
        if (titleIds[j] === lastSeenTitleId) {
          return false;
        }
      }
    }
  }

  return true;
};

export default LeftTimelineActions;
